import React, { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { FormattedDisplayName, FormattedMessage, useIntl } from 'react-intl'
import { Modal, NativeSyntheticEvent, Platform, Pressable, TextInputChangeEventData, ViewStyle } from 'react-native'
import ReactNativeCountryFlag from 'react-native-country-flag'

import {
  AsYouType,
  CountryCallingCode,
  CountryCode as CountryIsoCode,
  getCountryCallingCode,
  isSupportedCountry,
} from 'libphonenumber-js'
import { isEmpty, isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  convertToInternationalFormat,
  convertToNationalFormat,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import {
  closeModalButtonLabel,
  unsupportedCountryCodeErrorMessage,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { CountryValues } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import SearchCountryModal from './countrySelection/SearchCountryModal'
import { Flex1View } from '../../../src/templates/content/CommonViews'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { BaseInput } from '../../atoms/formElements/BaseInput'
import { CheckIcon } from '../../atoms/icons/CheckIcon'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { TooltipMaxWidth, TooltipPlacement, ToolTipTriggerConfig } from '../../atoms/tooltip/Tooltip'
import { IS_WEB } from '../../constants'
import { useAccessibilityFocus } from '../../hooks/useAccessibilityFocus'
import { useFocusState } from '../../hooks/useFocusState'
import { getInputFieldStyles } from '../../styles/commonStyles'
import { getTypeStyles } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

const ModalInnerBox = styled(Pressable)<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  backgroundColor: colors.backgroundPrimary,
  paddingTop: '24px',
  width: '100%',
  height: '100%',
}))

const ModalInnerButton = styled(Pressable)<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  backgroundColor: colors.backgroundPrimary,
  paddingTop: '24px',
  minWidth: '345px',
  height: '600px',
  shadowColor: colors.shadowLow,
  shadowOffset: {
    width: '0',
    height: '2',
  },
  shadowOpacity: '0.25',
  shadowRadius: '4',
  elevation: '5',
  borderRadius: '10px',
  cursor: 'default',
}))

const CountrySelectionDropdownButtonBox = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const CountryCodeBox = styled.View(({ theme }) => ({
  marginHorizontal: theme.spacing['8px'],
}))

const CountryRowBox = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginVertical: '8px',
})

const CountryFlagAndNameBox = styled.View({
  flexDirection: 'row',
})

const CountryNameBox = styled.View({
  marginHorizontal: '10px',
})

const CountryButtonAndPhoneInputBox = styled.View<{ inputBackgroundColor?: string }>(({ inputBackgroundColor }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: '48px',
  ...(inputBackgroundColor && { backgroundColor: inputBackgroundColor }),
}))

const CountryButtonBox = styled(Pressable)(({ theme }) => ({
  paddingRight: theme.spacing['8px'],
}))

const PhoneNumberBox = styled(Flex1View)<{ theme: ThemeType; hideCountrySelector?: boolean }>(
  ({ theme, hideCountrySelector }) => ({
    ...(!hideCountrySelector && { paddingLeft: theme.spacing['8px'] }),
    height: '38px',
  }),
)

const PhoneNumberTextInputBox = styled.TextInput({
  ...(IS_WEB && { outlineWidth: 0, outlineStyle: 'none' }),
})

const ModalOverallBox = styled(Flex1View)({
  justifyContent: 'center',
  alignItems: 'center',
})

const ModalOuterBoxBaseStyle = {
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(74,74,74,.25)',
  flex: 1,
}

const ModalOuterBoxButton = styled(Pressable)({
  ...ModalOuterBoxBaseStyle,
  cursor: 'default',
})

const ModalHeaderBox = styled.View({
  flexDirection: 'row',
  justifyContent: 'left',
  alignItems: 'center',
  margin: '24px',
})

const ModalCancelBox = styled(Pressable)({
  position: 'absolute',
  right: '0',
})

export interface InternationalPhoneNumberEntryProps {
  onFocus: () => void
  onBlur: () => void
  placeholder?: string
  error?: string
  name: string
  value: string
  active?: boolean
  label?: string
  customerCountryList: CountryValues[]
  userCountryIsoCode: CountryIsoCode
  onChange: (arg: string) => void
  onCountryIsoCodeChange?: (arg: CountryIsoCode) => void
  onCallingCodeChange?: (callingCode: string | undefined) => void
  linkUserCountryIsoCode?: boolean
  readOnly?: boolean
  baseInputStyle?: ViewStyle
  hideCountrySelector?: boolean
  hideInputFlag?: boolean
  toolTipContent?: string | ReactElement
  toolTipTriggerConfig?: ToolTipTriggerConfig
  toolTipPlacement?: TooltipPlacement
  toolTipSize?: TooltipMaxWidth
  enableAutoComplete?: boolean
  validateCallingCode?: boolean
  inputBackgroundColor?: string
  required?: boolean
}

const CountrySelectionDropdownButton = React.memo(
  ({
    selectedCountry,
    selectedCallingCode,
    customerCountryList,
    modalVisible,
    hideInputFlag,
    colors,
  }: {
    selectedCountry: CountryIsoCode
    selectedCallingCode?: string
    customerCountryList: CountryValues[]
    modalVisible: boolean
    hideInputFlag: boolean
    colors: ThemeType['colors']
  }) => {
    return (
      <CountrySelectionDropdownButtonBox>
        {!hideInputFlag && <ReactNativeCountryFlag isoCode={selectedCountry} size={18} />}
        {selectedCallingCode && (
          <CountryCodeBox>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <BodyText text={`+${selectedCallingCode}`} />
          </CountryCodeBox>
        )}
        {!isEmpty(customerCountryList) && (
          <ChevronIcon
            fillColor={colors.iconDefault}
            direction={modalVisible ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
          />
        )}
      </CountrySelectionDropdownButtonBox>
    )
  },
)

export const InternationalPhoneNumberEntry: FunctionComponent<InternationalPhoneNumberEntryProps> = ({
  label,
  value,
  placeholder,
  onChange = () => {},
  onCountryIsoCodeChange = () => {},
  onCallingCodeChange,
  onFocus,
  onBlur,
  error,
  name,
  active,
  customerCountryList,
  userCountryIsoCode,
  linkUserCountryIsoCode = false,
  readOnly,
  baseInputStyle,
  hideCountrySelector,
  hideInputFlag = false,
  toolTipContent,
  toolTipTriggerConfig,
  toolTipPlacement,
  toolTipSize,
  enableAutoComplete = false,
  validateCallingCode = true,
  inputBackgroundColor,
  required,
}) => {
  const { colors, fontFamilyMetadata, componentStyles, isAuroraTheme } = useTheme()
  const { isFocused, focusProps } = useFocusState({ onFocus, onBlur, active })
  const [focusRef] = useAccessibilityFocus({ active: isFocused, delay: 200 })
  const supportedCountryList = isNil(customerCountryList)
    ? []
    : customerCountryList.filter((country) => isSupportedCountry(country.countryIsoCode))
  const { formatMessage } = useIntl()
  const [modalVisible, setModalVisible] = useState(false)
  const parsedNumber = new AsYouType()
  parsedNumber.input(value)
  const initialCountryCode = parsedNumber.country ?? userCountryIsoCode ?? 'US'
  const [selectedCountry, setSelectedCountry] = useState<CountryIsoCode>(initialCountryCode)
  const [phoneNumber, setPhoneNumber] = useState<string>(convertToNationalFormat(value, [initialCountryCode]))
  const selectedCallingCode = useMemo((): CountryCallingCode | undefined => {
    try {
      return getCountryCallingCode(selectedCountry)
    } catch {
      return undefined
    }
  }, [selectedCountry])
  const selectedCountryObj = customerCountryList?.find((country) => country.countryIsoCode === selectedCountry)
  const typeStyles = getTypeStyles(colors, fontFamilyMetadata)
  const { lineHeight, ...prunedTypeStyles } = typeStyles.bodyDefault
  const isDisabled = isEmpty(customerCountryList) || readOnly
  const textInputStyles = {
    ...getInputFieldStyles({
      colors,
      fontFamilyMetadata,
      componentStyles,
      active: isFocused,
      readOnly,
      error: !!error,
    }),
    ...(isAuroraTheme && isDisabled && { backgroundColor: 'transparent' }),
  } as ViewStyle

  useEffect(() => {
    if (selectedCountry) {
      onChange(convertToInternationalFormat(phoneNumber, [selectedCountry]))
    }
  }, [onChange, phoneNumber, selectedCountry])

  useEffect(() => {
    if (linkUserCountryIsoCode && userCountryIsoCode && userCountryIsoCode !== selectedCountry) {
      setSelectedCountry(userCountryIsoCode)
    }
    // Only apply the selected country if linkUserCountryIsoCode enabled &
    // user country changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCountryIsoCode])

  useEffect(() => {
    onCallingCodeChange?.(selectedCallingCode)
  }, [onCallingCodeChange, selectedCallingCode])

  const callingCodeValidationError =
    validateCallingCode && !selectedCallingCode ? formatMessage(unsupportedCountryCodeErrorMessage) : undefined
  const displayError = error ?? callingCodeValidationError

  const errorMessageId = name ? `${name}-error-message` : ''

  const getCountryButtons = ({ countryIsoCode, countryName }: CountryValues, key?: number | string) => (
    <Pressable
      accessibilitySelected={IS_WEB ? countryIsoCode === selectedCountry : undefined}
      // ignoring this TS issue, can use 'option' for role with react-native-web
      // @ts-ignore next-line
      accessibilityRole={IS_WEB ? 'option' : undefined}
      key={key}
      testID={tID(`SearchCountryModal-button-${countryName}`)}
      onPress={() => {
        setSelectedCountry(countryIsoCode)
        onCountryIsoCodeChange(countryIsoCode)
        closeModal()
      }}
    >
      <CountryRowBox>
        <CountryFlagAndNameBox>
          <ReactNativeCountryFlag size={20} isoCode={countryIsoCode} />
          <CountryNameBox>
            <BodyText text={<FormattedDisplayName type='region' value={countryIsoCode} />} size={BodyTextSize.SMALL} />
          </CountryNameBox>
          {countryIsoCode === selectedCountry && <CheckIcon withCircle={false} />}
        </CountryFlagAndNameBox>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <BodyText text={`+${getCountryCallingCode(countryIsoCode)}`} size={BodyTextSize.SMALL} />
      </CountryRowBox>
    </Pressable>
  )

  const closeModal = () => setModalVisible(false)

  const modalContent = (
    <>
      <ModalHeaderBox>
        <Title
          text={
            <FormattedMessage
              defaultMessage='Country or territory code'
              description="Label for various countries' or territories' phone number prefixes"
            />
          }
          size={TitleSize.SMALL}
        />
        <ModalCancelBox onPress={closeModal}>
          <Title
            text={formatMessage(closeModalButtonLabel)}
            size={TitleSize.XSMALL}
            color={colors.textActive}
            noRole={true}
          />
        </ModalCancelBox>
      </ModalHeaderBox>
      <SearchCountryModal countryList={supportedCountryList} customCountryButtonDisplay={getCountryButtons} />
    </>
  )

  return (
    <BaseInput
      label={label}
      error={displayError}
      name={name}
      style={baseInputStyle}
      errorMessageId={errorMessageId}
      toolTipContent={toolTipContent}
      toolTipTriggerConfig={toolTipTriggerConfig}
      toolTipPlacement={toolTipPlacement}
      toolTipSize={toolTipSize}
      required={required}
    >
      <CountryButtonAndPhoneInputBox style={textInputStyles} inputBackgroundColor={inputBackgroundColor}>
        {hideCountrySelector ? undefined : (
          <CountryButtonBox
            ref={focusRef}
            testID={tID('CountryDropdown-Button')}
            onPress={() => {
              setModalVisible(!modalVisible)
            }}
            disabled={isDisabled}
            {...(IS_WEB && isAuroraTheme && isDisabled && { dataSet: { auroraDisabled: 'true' } })}
            accessibilityLabel={formatMessage(
              {
                defaultMessage: `Country or territory code {selectedCallingCode}, {selectedCountryName}`,
                description: 'Dropdown modal to change country or territory code',
              },
              { selectedCallingCode: selectedCallingCode, selectedCountryName: selectedCountryObj?.countryName },
            )}
            accessibilityRole='button'
            accessibilityExpanded={modalVisible}
            accessibilityHasPopup='menu'
            accessible
          >
            <CountrySelectionDropdownButton
              selectedCountry={selectedCountry}
              selectedCallingCode={selectedCallingCode}
              customerCountryList={customerCountryList}
              modalVisible={modalVisible}
              hideInputFlag={hideInputFlag}
              colors={colors}
            />
          </CountryButtonBox>
        )}
        {!hideCountrySelector && <Divider height={38} width={1} />}
        <PhoneNumberBox
          hideCountrySelector={hideCountrySelector}
          defaultFlex
          style={typeStyles.bodyDefault as ViewStyle}
        >
          <PhoneNumberTextInputBox
            testID={name}
            onChange={(e: NativeSyntheticEvent<TextInputChangeEventData>) => {
              setPhoneNumber(convertToNationalFormat(e.nativeEvent.text, [selectedCountry]))
            }}
            onFocus={focusProps.onFocus}
            onBlur={focusProps.onBlur}
            value={phoneNumber}
            keyboardType='number-pad'
            placeholder={placeholder}
            accessibilityLabel={formatMessage(
              {
                defaultMessage: `Phone number for {CountryName} without country code`,
                description: 'Accessibility label for phone number input field without the country code part',
              },
              { CountryName: selectedCountryObj?.countryName },
            )}
            {...(IS_WEB && { 'aria-describedby': errorMessageId })}
            placeholderTextColor={colors.inputTextPlaceholder}
            style={[prunedTypeStyles, { height: '100%' }, Platform.OS !== 'ios' && { lineHeight }]}
            autoComplete={enableAutoComplete ? 'tel-national' : 'off'}
            editable={!readOnly}
          />
        </PhoneNumberBox>
      </CountryButtonAndPhoneInputBox>
      <ModalOverallBox defaultFlex>
        <Modal animationType={IS_WEB ? 'fade' : 'slide'} transparent visible={modalVisible} onRequestClose={closeModal}>
          {!IS_WEB ? (
            <ModalOuterBoxButton onPressOut={closeModal}>
              <ModalInnerBox>{modalContent}</ModalInnerBox>
            </ModalOuterBoxButton>
          ) : (
            <ModalOuterBoxButton onPressOut={closeModal}>
              <ModalInnerButton>{modalContent}</ModalInnerButton>
            </ModalOuterBoxButton>
          )}
        </Modal>
      </ModalOverallBox>
    </BaseInput>
  )
}

export interface InternationalPhoneNumberEntryRFFProps extends FieldRenderProps<string> {
  label?: string
  placeholder?: string
  customerCountryList: CountryValues[]
  userCountryIsoCode: CountryIsoCode
  onCountryIsoCodeChange?: (arg: CountryIsoCode) => void
  onCallingCodeChange?: (callingCode: string | undefined) => void
  linkUserCountryIsoCode?: boolean
  readOnly?: boolean
  baseInputStyle?: ViewStyle
  hideCountrySelector?: boolean
  hideInputFlag?: boolean
  toolTipContent?: string | ReactElement
  toolTipTriggerConfig?: ToolTipTriggerConfig
  toolTipPlacement?: TooltipPlacement
  toolTipSize?: TooltipMaxWidth
  enableAutoComplete?: boolean
  validateCallingCode?: boolean
  inputBackgroundColor?: string
  required?: boolean
}

export const InternationalPhoneNumberEntryRFF: FunctionComponent<InternationalPhoneNumberEntryRFFProps> = ({
  input: { onChange, onFocus, onBlur, name, value },
  meta: { active, touched, error },
  label,
  placeholder,
  customerCountryList,
  onCountryIsoCodeChange,
  onCallingCodeChange,
  linkUserCountryIsoCode = false,
  userCountryIsoCode,
  readOnly,
  baseInputStyle,
  hideCountrySelector,
  hideInputFlag,
  toolTipContent,
  toolTipTriggerConfig,
  toolTipPlacement,
  toolTipSize,
  enableAutoComplete,
  validateCallingCode,
  inputBackgroundColor,
  required,
}) => (
  <InternationalPhoneNumberEntry
    label={label}
    placeholder={placeholder}
    onChange={onChange}
    onFocus={onFocus}
    onBlur={onBlur}
    error={touched && error}
    name={name}
    value={value}
    active={active}
    customerCountryList={customerCountryList}
    onCountryIsoCodeChange={onCountryIsoCodeChange}
    onCallingCodeChange={onCallingCodeChange}
    userCountryIsoCode={userCountryIsoCode}
    linkUserCountryIsoCode={linkUserCountryIsoCode}
    readOnly={readOnly}
    baseInputStyle={baseInputStyle}
    hideCountrySelector={hideCountrySelector}
    hideInputFlag={hideInputFlag}
    toolTipContent={toolTipContent}
    toolTipTriggerConfig={toolTipTriggerConfig}
    toolTipPlacement={toolTipPlacement}
    toolTipSize={toolTipSize}
    enableAutoComplete={enableAutoComplete}
    validateCallingCode={validateCallingCode}
    inputBackgroundColor={inputBackgroundColor}
    required={required}
  />
)
